import { Languages } from "@providers"

import type { LanguageISO } from "@providers"

export type PostElementBackground = "white" | "black" | "gray"

export const POST_TYPES = {
  post: "post",
  advice: "advice",
  story: "story",
  promo: "promo",
  editor_choice: "editor_choice",
}
export type PostUnion = keyof typeof POST_TYPES

export interface Element {
  content: string
  background: PostElementBackground
}

export interface Author {
  name: string
  image: string
}

export interface Category {
  id: number
  name: string
  slug: string
  lang: LanguageISO
  metaTitle: string
  metaDescription: string
  post_links?: PostModel[]
  posts?: PostModel[]
  seoH1?: string
}

export interface PostModel {
  OnDating: boolean
  id: string | number
  title: string
  slug: string
  lang: LanguageISO
  type: PostUnion
  type_title?: string
  description: string
  meta_title: string
  meta_description: string
  caption: string
  href?: string
  published_at: Date | null
  slider: boolean
  author: Author | null
  elements: Element[]
  category: Category | null
  branch_link: string | null
  banner: ArticleBannerStructure | null
  image_preview: ImageWithFormats
  image_cover: ImageWithFormats
}

export interface ArticleBannerStructure {
  action_text: string
  background_color: string
  id: number
  link: string
  link_appgallery: string
  playmarket_button_text: string
  appgallery_button_text: string
  locale: string
  name: string
  picture: {
    alternativeText: string
    width: number
    height: number
    url: string
  }
  title: string
}

interface ArticleImage {
  url: string
  width: number
  height: number
  alternativeText: string
  caption: string
}

interface ArticleSerializerParams {
  post: {
    id: string | number
    title: string
    lang: string
    type?: string
    type_title?: string
    description?: string
    meta_title?: string
    meta_description?: string
    caption?: string
    slug?: string
    href?: string
    published_at?: string | null
    in_slider?: boolean
    category: Category | null
    OnDating: boolean
    branch_link: string
    author: {
      name: string
      image: string
    } | null
    elements?: Element[] | null
    banner: ArticleBannerStructure | null
    image_cover: ImageWithFormats | null
    image_preview: ImageWithFormats | null
  }
  shorted?: boolean
}

export type ImageFormatName =
  | "article_preview"
  | "article_cover_mobile"
  | "article_cover_desktop"
  | "article_cover_mobile_x2"
  | "article_cover_desktop_x2"
  | "article_preview_x2"
  | "article_preview_original"
  | "article_cover_original"

export interface ImageWithFormats extends ArticleImage {
  formats: Partial<Record<ImageFormatName, ArticleImage>>
}

export interface ImageSerializerParams {
  alternativeText: string
  caption: string
  url: string
  formats: {
    [formatName: string]: ArticleImage
  }
}

const emptyImage: ImageWithFormats = {
  url: "",
  caption: "",
  alternativeText: "",
  width: 0,
  height: 0,
  formats: {},
}

function ImageSerializer(image: ImageWithFormats | null): ImageWithFormats {
  if (!image) {
    return emptyImage
  }

  return {
    ...image,
    formats: {
      article_preview: image.formats.article_preview,
      article_cover_mobile: image.formats.article_cover_mobile,
      article_cover_desktop: image.formats.article_cover_desktop,
      article_preview_x2: image.formats.article_preview_x2,
      article_cover_mobile_x2: image.formats.article_cover_mobile_x2,
      article_cover_desktop_x2: image.formats.article_cover_desktop_x2,
    },
  }
}

export function ArticleSerializer(params: ArticleSerializerParams): PostModel {
  const { post } = params

  const resolve: PostModel = {
    id: post.id,
    title: post.title,
    lang: Languages[post.lang].id,
    type: post.type ?? "post",
    type_title: post.type_title ?? "",
    description: post.description ?? "",
    meta_title: post.meta_title ?? "",
    meta_description: post.meta_description ?? "",
    caption: post.caption ?? "",
    slug: post.slug ?? "",
    href: post.href ?? "",
    published_at: post.published_at ?? null,
    slider: post.in_slider ?? false,
    category: post.category,
    OnDating: post.OnDating,
    branch_link: post.branch_link,
    author: post.author
      ? {
          name: post.author.name,
          image: post.author.image,
        }
      : null,
    elements: post.elements ?? [],
    banner: post.banner,
    image_cover: ImageSerializer(post.image_cover),
    image_preview: ImageSerializer(post.image_preview),
  } as PostModel

  return resolve
}
